import { sceneKey, type SceneKey, type SceneRef } from './scene-key';

export type { SceneRef } from './scene-key';

/**
 * 场景隔离的几何保存队列（M3b MEDIUM-1 修复）。
 *
 * 不依赖 React：纯逻辑类，方便直接单元测试，也方便 `useGeometrySaver` 之外
 * 复用。**这个文件是 M3a 独立复审 MEDIUM-1 的根因所在层**——旧实现的
 * pending Map/定时器不区分场景，`flush` 又通过闭包捕获 `parentId`，导致
 * 场景切换或组件卸载时，待保存的几何操作要么被送去了错误的场景（服务端拒绝
 * 但前端不处理该结果），要么在卸载时被直接清空、一个 PATCH 都没发出去。
 *
 * 修复方式：待保存操作按场景分桶。每个场景有自己独立的 debounce 定时器；
 * 离开某个场景（父组件在场景切换或卸载时调用 `flushScene`/`flushAll`）会
 * **立即**把该场景累积的操作发出去，声明的是那个场景**自己的**标识，不会被
 * 后来切换到的新场景污染。
 *
 * M3B 独立复审第二轮 NEW-2：场景标识不只是 `parentId`——`parentId` 相同（典型
 * 情况是两个不同仓库都在看各自的根场景，`parentId` 都是 `null`）不代表是
 * 同一个场景。分桶 key 统一用 `./scene-key.ts` 的 `sceneKey({warehouseId,
 * parentId})`，每个桶随身带着自己的 `SceneRef`（而不是靠反解 key 拿回
 * `parentId`），`send`/`onSettled` 回调因此总能拿到这批操作真正所属的
 * 完整场景标识。
 */

/**
 * 排队中的几何操作——**故意没有 `version` 字段**（M3B 独立复审第三轮
 * BLOCKER 2）：版本不在排队那一刻决定，而是在 PATCH 真正发出的那一刻由
 * `resolveVersion` 从权威账本（见 `geometry-authority.ts`）解析。排队和发送
 * 之间任何一批保存结果落地，都会先推进账本，后发出的请求自然带上最新版本，
 * 不需要等 React 渲染。
 */
export interface PendingGeometryOp {
  readonly locationId: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly locked?: boolean;
}

/** 真正上线的操作：`PendingGeometryOp` + 发送时刻解析出的 `version`。 */
export interface GeometryOp extends PendingGeometryOp {
  readonly version: number;
}

export type GeometryResult =
  | { readonly locationId: string; readonly ok: true; readonly version: number }
  | {
      readonly locationId: string;
      readonly ok: false;
      readonly code: string;
      readonly current?: unknown;
    };

export type SaveOutcome =
  | { readonly ok: true; readonly results: readonly GeometryResult[] }
  | { readonly ok: false; readonly error: unknown; readonly locationIds: readonly string[] };

interface SceneBucket {
  readonly scene: SceneRef;
  readonly ops: Map<string, PendingGeometryOp>;
}

export interface SceneSaveQueueOptions {
  readonly debounceMs: number;
  /**
   * 发送时刻的版本权威（M3B 独立复审第三轮 BLOCKER 2）：每个操作在 PATCH
   * 发出前一刻调用它拿最新已知版本。实现方（页面）背后是同步更新的
   * `VersionLedger`——每一批保存结果在响应处理器里就推进账本，不等渲染。
   */
  readonly resolveVersion: (locationId: string) => number;
  readonly send: (
    scene: SceneRef,
    ops: readonly GeometryOp[],
  ) => Promise<{ results: GeometryResult[] }>;
  /** 每次某个场景的一批操作落地（成功或失败）后触发，携带该批操作所属的场景。 */
  readonly onSettled: (scene: SceneRef, outcome: SaveOutcome) => void;
  /** pending/saving 状态变化时触发，供 React 层重新渲染。 */
  readonly onPendingChange: () => void;
}

export class SceneSaveQueue {
  private readonly pending = new Map<SceneKey, SceneBucket>();
  private readonly timers = new Map<SceneKey, ReturnType<typeof setTimeout>>();
  private inFlight = 0;

  constructor(private readonly opts: SceneSaveQueueOptions) {}

  get saving(): boolean {
    return this.inFlight > 0;
  }

  pendingCount(scene: SceneRef): number {
    return this.pending.get(sceneKey(scene))?.ops.size ?? 0;
  }

  get hasAnyPending(): boolean {
    for (const bucket of this.pending.values()) if (bucket.ops.size > 0) return true;
    return false;
  }

  /** 同一对象在 debounce 窗口内被多次拖动只保留最后一次；只影响自己所在的场景。 */
  queue(scene: SceneRef, op: PendingGeometryOp): void {
    const key = sceneKey(scene);
    let bucket = this.pending.get(key);
    if (!bucket) {
      bucket = { scene, ops: new Map() };
      this.pending.set(key, bucket);
    }
    bucket.ops.set(op.locationId, op);
    this.opts.onPendingChange();

    const existing = this.timers.get(key);
    if (existing !== undefined) clearTimeout(existing);
    this.timers.set(
      key,
      setTimeout(() => void this.flushScene(scene), this.opts.debounceMs),
    );
  }

  /**
   * 立即（跳过 debounce）发出某个场景累积的操作，声明的场景就是这个场景
   * 自己的——不会因为调用方此刻已经在显示别的场景（甚至别的仓库）而被替换。
   * 场景切换、仓库切换与组件卸载都要调用它，这样"离开时还没保存"的几何变更
   * 不会被静默丢弃。队列为空时是安全的空操作。
   */
  async flushScene(scene: SceneRef): Promise<void> {
    const key = sceneKey(scene);
    const timer = this.timers.get(key);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(key);
    }
    const bucket = this.pending.get(key);
    if (!bucket || bucket.ops.size === 0) return;
    // 发送时刻才解析 version（BLOCKER 2）：排队和现在之间落地的任何保存结果
    // 都已经推进了权威账本，这里拿到的就是最新已知版本。
    const ops: GeometryOp[] = [...bucket.ops.values()].map((op) => ({
      ...op,
      version: this.opts.resolveVersion(op.locationId),
    }));
    this.pending.delete(key);
    this.opts.onPendingChange();

    this.inFlight++;
    this.opts.onPendingChange();
    try {
      // keepalive：卸载期间发起的请求也要送达（见 apiFetch 的 keepalive 说明）。
      const res = await this.opts.send(bucket.scene, ops);
      this.opts.onSettled(bucket.scene, { ok: true, results: res.results });
    } catch (error) {
      // M3B 独立复审 H-2：整批请求失败（网络错误、409 租约丢失……）时也要带上
      // 这批操作涉及的 locationId——调用方要靠这个把对应对象的本地乐观覆盖层
      // 清掉、回落到服务端权威几何，而不是让画布继续画着一个从未真正保存
      // 成功的位置。
      this.opts.onSettled(bucket.scene, {
        ok: false,
        error,
        locationIds: ops.map((o) => o.locationId),
      });
    } finally {
      this.inFlight--;
      this.opts.onPendingChange();
    }
  }

  /** 卸载编辑器时的兜底：把**所有**还有未保存操作的场景一次性冲掉，不只是当前场景。 */
  async flushAll(): Promise<void> {
    const buckets = [...this.pending.values()];
    await Promise.all(buckets.map((b) => this.flushScene(b.scene)));
  }

  dispose(): void {
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();
  }
}
